/**
 * API client for Swarm Learning Loop.
 */

const API_BASE = import.meta.env.VITE_API_BASE || (import.meta.env.DEV ? 'http://localhost:8001' : '')

export interface RunSummary {
  run_id: string
  date: string
  goal: string
  overall_score: number
  overall_status: 'PASS' | 'WARN' | 'FAIL'
  total_tasks: number
  passed_tasks: number
  warned_tasks: number
  failed_tasks: number
}

export interface DashboardStats {
  total_runs: number
  passed_runs: number
  warned_runs: number
  failed_runs: number
  success_rate: number
  average_score: number
  total_tasks: number
  average_tasks_per_run: number
}

export interface AgentPerformance {
  agent: string
  total_tasks: number
  success_rate: number
  average_score: number
  passed_tasks: number
  warned_tasks: number
  failed_tasks: number
}

export interface DashboardResponse {
  stats: DashboardStats
  agent_performance: AgentPerformance[]
  recent_runs: RunSummary[]
  vector_store_stats?: {
    total_experiences: number
    embedding_model_available: boolean
    chromadb_available: boolean
  }
}

export interface SearchRequest {
  query: string
  n_results?: number
  filter_status?: string
}

export interface SearchResult {
  run_id: string
  goal: string
  overall_score: number
  overall_status: string
  similarity_score?: number
}

export interface RunDetails {
  run_id: string
  date: string
  eval: any
  dag: any
  summary?: string
}

class SwarmAPI {
  private baseUrl: string

  constructor(baseUrl: string = API_BASE) {
    this.baseUrl = baseUrl
  }

  async getDashboard(days: number = 30): Promise<DashboardResponse> {
    const url = `${this.baseUrl}/api/v1/swarm/dashboard?days=${days}`
    const response = await fetch(url)
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error(`Endpoint not found (404). The Swarm Dashboard API may not be deployed yet. URL: ${url}`)
      }
      throw new Error(`Failed to get dashboard (${response.status}): ${response.statusText}`)
    }
    return response.json()
  }

  async listRuns(days: number = 30, limit: number = 50): Promise<RunSummary[]> {
    const response = await fetch(`${this.baseUrl}/api/v1/swarm/runs?days=${days}&limit=${limit}`)
    if (!response.ok) {
      throw new Error(`Failed to list runs: ${response.statusText}`)
    }
    return response.json()
  }

  async getRunDetails(runId: string): Promise<RunDetails> {
    const response = await fetch(`${this.baseUrl}/api/v1/swarm/runs/${runId}`)
    if (!response.ok) {
      throw new Error(`Failed to get run details: ${response.statusText}`)
    }
    return response.json()
  }

  async searchExperiences(request: SearchRequest): Promise<SearchResult[]> {
    const response = await fetch(`${this.baseUrl}/api/v1/swarm/search`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    })
    if (!response.ok) {
      throw new Error(`Search failed: ${response.statusText}`)
    }
    return response.json()
  }

  async getMetrics(days: number = 30): Promise<any> {
    const response = await fetch(`${this.baseUrl}/api/v1/swarm/metrics?days=${days}`)
    if (!response.ok) {
      throw new Error(`Failed to get metrics: ${response.statusText}`)
    }
    return response.json()
  }

  async getVectorStoreStats(): Promise<any> {
    const response = await fetch(`${this.baseUrl}/api/v1/swarm/vector-store/stats`)
    if (!response.ok) {
      throw new Error(`Failed to get vector store stats: ${response.statusText}`)
    }
    return response.json()
  }

  async createRun(request: {
    goal: string
    tasks: Array<{
      id: string
      name: string
      description?: string
      owner_agent_role?: string
      inputs?: string[]
      outputs?: string[]
      dependencies?: string[]
      acceptance_criteria?: string[]
      retry_policy?: any
      timeout?: number
      priority?: number
      prompt_template?: string
    }>
    context?: any
  }): Promise<{ run_id: string; status: string; message: string }> {
    const response = await fetch(`${this.baseUrl}/api/v1/swarm/runs/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    })
    if (!response.ok) {
      throw new Error(`Failed to create run: ${response.statusText}`)
    }
    return response.json()
  }
}

export const swarmAPI = new SwarmAPI()

